import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Loader } from "../ui/loader/loader";
import { canAccessRouteRule } from "../../config/accessControl";
import { useAccess } from "../../context/AccessContext";
import { loadRestorableUserRoute } from "../../hooks/useUserAppState";

const ProtectedRoute = ({ children, routeRule }) => {
  const location = useLocation();
  const {
    isAuthenticated,
    isAccessLoading,
    permissions,
    selectedPrivilegeGroups,
    roleName,
    getDefaultRoute,
    accessData,
  } = useAccess();

  // Phase 3: when an already-authenticated user hits / or /login, restore
  // their last route (RBAC-checked) instead of always using getDefaultRoute.
  const [publicRedirectTo, setPublicRedirectTo] = useState(null);
  const [isResolvingPublicRedirect, setIsResolvingPublicRedirect] = useState(
    Boolean(routeRule?.publicOnly && isAuthenticated)
  );

  useEffect(() => {
    if (!routeRule?.publicOnly || !isAuthenticated || isAccessLoading) {
      setIsResolvingPublicRedirect(false);
      return undefined;
    }

    let cancelled = false;
    setIsResolvingPublicRedirect(true);

    const access = {
      permissions: accessData?.permissions || permissions,
      selectedPrivilegeGroups:
        accessData?.selectedPrivilegeGroups || selectedPrivilegeGroups,
      roleName: accessData?.roleName || roleName,
    };

    loadRestorableUserRoute(access)
      .then((restored) => {
        if (!cancelled) {
          setPublicRedirectTo(restored || getDefaultRoute());
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPublicRedirectTo(getDefaultRoute());
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsResolvingPublicRedirect(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    routeRule?.publicOnly,
    isAuthenticated,
    isAccessLoading,
    permissions,
    selectedPrivilegeGroups,
    roleName,
    accessData,
    getDefaultRoute,
  ]);

  if (isAccessLoading) {
    return <Loader />;
  }

  if (routeRule?.publicOnly) {
    if (!isAuthenticated) {
      return children;
    }

    if (isResolvingPublicRedirect || !publicRedirectTo) {
      return <Loader />;
    }

    return <Navigate to={publicRedirectTo} replace />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  const canAccessRoute = canAccessRouteRule(routeRule, {
    permissions,
    selectedPrivilegeGroups,
    roleName,
  });

  if (!canAccessRoute) {
    const fallbackRoute = getDefaultRoute();

    if (fallbackRoute && fallbackRoute !== location.pathname) {
      return <Navigate to={fallbackRoute} replace />;
    }

    return (
      <div className="white_bg_box" style={{ margin: "20px" }}>
        No accessible pages are assigned to this user yet.
      </div>
    );
  }

  return children;
};

export default ProtectedRoute;
